import * as THREE from 'three'
import {
  looseKey,
  normalizeGlbName,
  stripInstanceSuffix,
  stripInstanceSuffixes,
  stripStepSuffix,
  stripTrailingIndex,
} from '@/utils/glbNameDerivations'

export type MeshHint = {
  articleNumber: string
  glbArticleNames?: string[]
  requiredAncestors: string[]
  milliQuantity?: number
  consumable?: boolean
  mismatchExempt?: boolean
  instanceIndexes?: number[] | null
}

export type SelectableHint = MeshHint & { ebomPartId: string; promoted: boolean }

const hintArticleSets = new WeakMap<MeshHint, Set<string>>()

const EXPECTED_SET_CACHE_LIMIT = 50_000
const expectedAncestorSets = new Map<string, Set<string>>()

function expectedSetFor(expected: string, mapped: string | undefined): Set<string> {
  const aliased = mapped !== undefined && mapped !== expected
  const key = aliased ? `${expected}\0${mapped}` : expected
  let set = expectedAncestorSets.get(key)
  if (!set) {
    set = aliased ? new Set([expected, mapped!]) : new Set([expected])
    if (expectedAncestorSets.size >= EXPECTED_SET_CACHE_LIMIT) expectedAncestorSets.clear()
    expectedAncestorSets.set(key, set)
  }
  return set
}

function articleSetFor(hint: MeshHint): Set<string> {
  let set = hintArticleSets.get(hint)
  if (!set) {
    set = new Set(hint.glbArticleNames ?? [hint.articleNumber])
    hintArticleSets.set(hint, set)
  }
  return set
}

export function nameMatchesArticle(
  name: string,
  articleSet: Set<string>,
  strict: boolean = false,
  exactArticleMatches?: Set<string>,
): boolean {
  if (articleSet.has(name)) return true
  const norm = normalizeGlbName(name)
  if (articleSet.has(norm)) return true

  const stepCleaned = stripStepSuffix(name)
  if (stepCleaned !== name && stepCleaned !== norm && articleSet.has(stepCleaned)) return true

  const looseName = looseKey(name)
  for (const article of articleSet) {
    if (looseKey(article) === looseName) return true
  }

  if (!exactArticleMatches?.has(name)) {
    for (const candidate of [name, stepCleaned, norm]) {
      for (const article of articleSet) {
        if (candidate.length > article.length && candidate.startsWith(article)) {
          if (/[_^<\s]/.test(candidate[article.length])) return true
        }
      }
    }
  }

  if (strict) return false

  const normNoInst = stripTrailingIndex(norm)
  if (normNoInst !== norm && articleSet.has(normNoInst)) return true
  const stepCleanedNoInst = stripInstanceSuffix(stepCleaned)
  if (
    stepCleanedNoInst !== stepCleaned &&
    articleSet.has(stepCleanedNoInst) &&
    !exactArticleMatches?.has(stepCleanedNoInst)
  )
    return true

  for (const article of articleSet) {
    const stripped = stripInstanceSuffixes(article)
    if (stripped !== article && (stripped === name || stripped === norm)) return true
  }

  const looseNorm = looseKey(norm)
  const looseStepCleaned = looseKey(stepCleaned)
  const looseNormNoInst = looseKey(normNoInst)
  for (const article of articleSet) {
    const la = looseKey(article)
    if (la === looseNorm || la === looseStepCleaned) return true
    if (looseNormNoInst !== looseNorm && la === looseNormNoInst) return true
  }
  return false
}

const PREFIX_BOUNDARY = /[_^<\s]/

function pushTo(map: Map<string, string[]>, key: string, name: string) {
  const names = map.get(key)
  if (!names) map.set(key, [name])
  else if (names[names.length - 1] !== name) names.push(name)
}

// Inverts nameMatchesArticle: every name is filed under each form that function compares an
// article against, so an article finds its candidate names without testing all of them. Large
// models have thousands of names and EBOMs thousands of articles, too many for a cross product.
export class GlbNameLookup {
  private readonly byExactKey = new Map<string, string[]>()
  private readonly byLooseKey = new Map<string, string[]>()
  private readonly order = new Map<string, number>()

  constructor(names: Iterable<string>) {
    for (const name of names) {
      this.order.set(name, this.order.size)
      const norm = normalizeGlbName(name)
      const stepCleaned = stripStepSuffix(name)
      const normNoInst = stripTrailingIndex(norm)
      for (const key of [name, norm, stepCleaned, normNoInst, stripInstanceSuffix(stepCleaned)])
        pushTo(this.byExactKey, key, name)
      for (const candidate of [name, stepCleaned, norm]) {
        for (let i = 0; i < candidate.length; i++) {
          if (PREFIX_BOUNDARY.test(candidate[i]))
            pushTo(this.byExactKey, candidate.slice(0, i), name)
        }
      }
      for (const key of [name, norm, stepCleaned, normNoInst])
        pushTo(this.byLooseKey, looseKey(key), name)
    }
  }

  /**
   * A superset of the names matching any article, in the order they were given; confirm each
   * with nameMatchesArticle.
   */
  candidateNames(articleSet: Set<string>): string[] {
    const names = new Set<string>()
    for (const article of articleSet) {
      for (const n of this.byExactKey.get(article) ?? []) names.add(n)
      for (const n of this.byExactKey.get(stripInstanceSuffixes(article)) ?? []) names.add(n)
      for (const n of this.byLooseKey.get(looseKey(article)) ?? []) names.add(n)
    }
    return [...names].sort((a, b) => this.order.get(a)! - this.order.get(b)!)
  }
}

export class HintIndex<T extends MeshHint> {
  private readonly byName = new Map<string, number[]>()
  private readonly seen = new Set<number>()

  constructor(
    private readonly hints: T[],
    ancestorNames: Iterable<string>,
    exactArticleMatches: Set<string>,
  ) {
    const lookup = new GlbNameLookup(ancestorNames)
    for (let i = 0; i < hints.length; i++) {
      const articleSet = articleSetFor(hints[i])
      for (const name of lookup.candidateNames(articleSet)) {
        if (!nameMatchesArticle(name, articleSet, false, exactArticleMatches)) continue
        const matching = this.byName.get(name)
        if (matching) matching.push(i)
        else this.byName.set(name, [i])
      }
    }
  }

  /** The meshes each hint has a matching ancestor in, by hint position and in mesh order. */
  meshesPerHint(
    allMeshes: THREE.Mesh[],
    meshAncestorNames: Map<THREE.Mesh, string[]>,
  ): THREE.Mesh[][] {
    const perHint: THREE.Mesh[][] = this.hints.map(() => [])
    for (const m of allMeshes) {
      this.seen.clear()
      for (const name of meshAncestorNames.get(m) ?? []) {
        for (const i of this.byName.get(name) ?? []) this.seen.add(i)
      }
      for (const i of this.seen) perHint[i].push(m)
    }
    return perHint
  }

  candidatesFor(ancestors: string[]): T[] {
    this.seen.clear()
    for (const name of ancestors) {
      const matching = this.byName.get(name)
      if (matching) for (const i of matching) this.seen.add(i)
    }
    if (this.seen.size === 0) return []
    // Ascending index restores the caller's hint ordering.
    return [...this.seen].sort((a, b) => a - b).map((i) => this.hints[i])
  }
}

export function distinctAncestorNames(
  allMeshes: THREE.Mesh[],
  meshAncestorNames: Map<THREE.Mesh, string[]>,
): Set<string> {
  const names = new Set<string>()
  for (const m of allMeshes) {
    for (const n of meshAncestorNames.get(m) ?? []) names.add(n)
  }
  return names
}

export function buildGlbNameMapping(
  allMeshes: THREE.Mesh[],
  meshAncestorNames: Map<THREE.Mesh, string[]>,
  hints: MeshHint[],
  exactArticleMatches: Set<string>,
): Map<string, string> {
  const emptyMapping = new Map<string, string>()
  const mapping = new Map<string, string>()
  // Only meshes with an ancestor matching the hint's article can match it at all.
  const meshesPerHint = new HintIndex(
    hints,
    distinctAncestorNames(allMeshes, meshAncestorNames),
    exactArticleMatches,
  ).meshesPerHint(allMeshes, meshAncestorNames)

  for (const [hintIndex, hint] of hints.entries()) {
    if (hint.requiredAncestors.length === 0) continue
    const hintMeshes = meshesPerHint[hintIndex]

    // If strict matching already works for this hint, no mapping needed.
    const alreadyMatches = hintMeshes.some((m) => {
      const a = meshAncestorNames.get(m)
      return a && matchesHintsWithMapping(a, [hint], emptyMapping, exactArticleMatches)
    })
    if (alreadyMatches) continue

    const articleSet = articleSetFor(hint)
    for (const mesh of hintMeshes) {
      const ancestors = meshAncestorNames.get(mesh)
      if (!ancestors) continue

      const pos = ancestors.findIndex((n) =>
        nameMatchesArticle(n, articleSet, false, exactArticleMatches),
      )
      if (pos < 0) continue

      let p = pos
      while (
        p < ancestors.length &&
        nameMatchesArticle(ancestors[p], articleSet, false, exactArticleMatches)
      )
        p++

      let valid = true
      const candidates = new Map<string, string>()
      for (let i = hint.requiredAncestors.length - 1; i >= 0; i--) {
        if (p >= ancestors.length) {
          valid = false
          break
        }
        const expected = hint.requiredAncestors[i]
        const actual = ancestors[p]
        if (!nameMatchesArticle(actual, new Set([expected]), true, exactArticleMatches)) {
          candidates.set(expected, actual)
        }
        p++
      }
      if (valid) {
        for (const [k, v] of candidates) mapping.set(k, v)
        break
      }
    }
  }

  return mapping
}

export function matchesHintsWithMapping(
  ancestors: string[],
  hints: MeshHint[],
  glbMapping: Map<string, string>,
  exactArticleMatches?: Set<string>,
): boolean {
  return hints.some((hint) => {
    const articleSet = articleSetFor(hint)

    let pos = ancestors.findIndex((n) =>
      nameMatchesArticle(n, articleSet, false, exactArticleMatches),
    )
    if (pos < 0) return false
    if (hint.requiredAncestors.length === 0) return true

    while (
      pos < ancestors.length &&
      nameMatchesArticle(ancestors[pos], articleSet, false, exactArticleMatches)
    )
      pos++

    for (let i = hint.requiredAncestors.length - 1; i >= 0; i--) {
      const expected = hint.requiredAncestors[i]
      const expectedSet = expectedSetFor(expected, glbMapping.get(expected))
      if (
        pos >= ancestors.length ||
        !nameMatchesArticle(ancestors[pos], expectedSet, true, exactArticleMatches)
      )
        return false
      pos++
    }
    return true
  })
}

export function bestMatchingHint<T extends MeshHint>(
  ancestors: string[],
  hints: T[],
  glbMapping: Map<string, string>,
  exactArticleMatches: Set<string>,
): T | null {
  let best: T | null = null
  let bestDepth = Infinity
  for (const hint of hints) {
    if (!matchesHintsWithMapping(ancestors, [hint], glbMapping, exactArticleMatches)) continue
    const articleSet = articleSetFor(hint)
    const matchIndex = ancestors.findIndex((n) =>
      nameMatchesArticle(n, articleSet, false, exactArticleMatches),
    )
    const depth = matchIndex < 0 ? Infinity : matchIndex
    if (
      !best ||
      depth < bestDepth ||
      (depth === bestDepth && hint.requiredAncestors.length > best.requiredAncestors.length)
    ) {
      best = hint
      bestDepth = depth
    }
  }
  return best
}
